import { useEffect, useRef, useState } from 'react'
import type { RequirementUserStory } from '@ispot/shared'
import type { SectionEditorProps } from './types'

type Value = RequirementUserStory | string | undefined

interface Props extends SectionEditorProps<Value> {}

/**
 * "As a [persona], I want [action], so that [outcome]" structured editor.
 *
 * The persona slot reads from `requirement.personas` (the link table) and
 * falls back to the legacy `metadata.userStory.role` string. Action and
 * outcome are inline editable spans with debounced commits.
 *
 * Persona PICKING is a separate flow (existing in the detail panel today —
 * stays where it is). When no persona is linked, we show a placeholder chip
 * that prompts the user to link one.
 */
export function UserStorySection({ value, onChange, requirement }: Props) {
  const initial = normalizeValue(value)
  const persona = requirement.personas[0]
  const personaName = persona?.role?.trim()
    || persona?.name?.trim()
    || (typeof requirement.metadata?.linkedPersona === 'object' ? requirement.metadata.linkedPersona?.name?.trim() : undefined)
    || initial.role
    || ''

  const [want, setWant] = useState(initial.want ?? '')
  const [benefit, setBenefit] = useState(initial.benefit ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const v = normalizeValue(value)
    setWant(v.want ?? '')
    setBenefit(v.benefit ?? '')
  }, [value])

  function commit(nextWant: string, nextBenefit: string) {
    const us: RequirementUserStory = {
      role: personaName || '',
      want: nextWant,
      benefit: nextBenefit,
    }
    onChange(us)
  }

  function scheduleCommit(nextWant: string, nextBenefit: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => commit(nextWant, nextBenefit), 600)
  }

  return (
    <div
      style={{
        borderLeft: '3px solid var(--color-solution)',
        background: 'var(--color-warm-100)',
        borderRadius: '0 4px 4px 0',
        padding: '12px 14px',
      }}
    >
      <p
        className="m-0"
        style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: 'var(--color-fg)',
        }}
      >
        <span style={{ color: 'var(--color-fg-muted)' }}>As a </span>
        <PersonaChip name={personaName} />
        <span style={{ color: 'var(--color-fg-muted)' }}>, I want </span>
        <InlineSlot
          value={want}
          placeholder="this thing to happen"
          onChange={(next) => { setWant(next); scheduleCommit(next, benefit) }}
          onCommit={() => commit(want, benefit)}
        />
        <span style={{ color: 'var(--color-fg-muted)' }}>, so that </span>
        <InlineSlot
          value={benefit}
          placeholder="this outcome is achieved"
          onChange={(next) => { setBenefit(next); scheduleCommit(want, next) }}
          onCommit={() => commit(want, benefit)}
        />
        <span style={{ color: 'var(--color-fg-muted)' }}>.</span>
      </p>
    </div>
  )
}

/** Persona display chip. Read-only here — picking lives in the meta strip. */
function PersonaChip({ name }: { name: string }) {
  if (!name) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0 7px',
          borderRadius: 4,
          background: 'transparent',
          color: 'var(--color-fg-muted)',
          fontStyle: 'italic',
          border: '1px dashed var(--color-border-strong)',
          fontSize: 13,
        }}
        title="Link a persona via the meta strip above"
      >
        unlinked persona
      </span>
    )
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        borderRadius: 4,
        background: 'var(--color-accent-50, var(--color-warm-100))',
        color: 'var(--color-accent)',
        fontWeight: 500,
        fontSize: 13.5,
      }}
    >
      {name}
    </span>
  )
}

/** Inline editable text slot — looks like prose, behaves like an input. */
function InlineSlot({
  value, placeholder, onChange, onCommit,
}: {
  value: string; placeholder: string;
  onChange: (next: string) => void;
  onCommit: () => void;
}) {
  return (
    <span
      contentEditable
      suppressContentEditableWarning
      onInput={(e) => onChange((e.target as HTMLElement).innerText)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLElement).blur()
        }
      }}
      data-placeholder={placeholder}
      className="inline-slot"
      style={{
        outline: 'none',
        borderBottom: '1px dashed var(--color-border-strong)',
        paddingBottom: 1,
        minWidth: '4ch',
        whiteSpace: 'pre-wrap',
      }}
      // Render value as the initial child via key to avoid React managing contentEditable text.
      key={value === '' ? 'empty' : 'has'}
      ref={(el) => {
        if (el && el.innerText !== value) el.innerText = value
      }}
    />
  )
}

function normalizeValue(v: Value): RequirementUserStory {
  if (!v) return { role: '', want: '', benefit: '' }
  if (typeof v === 'string') return { role: '', want: v, benefit: '' }
  return { role: v.role ?? '', want: v.want ?? '', benefit: v.benefit ?? '' }
}
